import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export interface CreateNotificationData {
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'announcement';
  target_audience: 'all' | 'company_admins' | 'specific_companies' | 'specific_users';
  target_company_ids?: string[];
  target_user_ids?: string[];
  expires_at?: string;
  metadata?: {
    ticket_id?: string;
    interaction_type?: 'message' | 'status_change' | 'assignment' | 'priority_change';
    redirect_url?: string;
    [key: string]: any;
  };
}

export function useCreateNotification() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: CreateNotificationData) => {
      if (!user?.id) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("system_notifications")
        .insert({
          title: data.title,
          content: data.content,
          type: data.type,
          target_audience: data.target_audience,
          target_company_ids: data.target_company_ids || null,
          target_user_ids: data.target_user_ids || null,
          expires_at: data.expires_at || null,
          metadata: data.metadata || null,
          created_by: user.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["system-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unread-notification-count"] });
    },
  });
}

export function useAdminNotifications() {
  return useQuery({
    queryKey: ["admin-notifications"],
    queryFn: async () => {
      // Primeira consulta: buscar notificações
      const { data: notifications, error: notificationsError } = await supabase
        .from("system_notifications")
        .select(`
          id,
          title,
          content,
          type,
          target_audience,
          target_company_ids,
          target_user_ids,
          expires_at,
          is_active,
          created_at,
          created_by,
          metadata
        `)
        .order("created_at", { ascending: false });

      if (notificationsError) throw notificationsError;
      if (!notifications?.length) return [];

      // Segunda consulta: buscar profiles dos criadores
      const creatorIds = [...new Set(notifications.map(n => n.created_by).filter(Boolean))];
      
      if (creatorIds.length === 0) {
        return notifications.map(notification => ({
          ...notification,
          profiles: null
        }));
      }

      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", creatorIds);

      if (profilesError) throw profilesError;

      // Combinar os dados
      return notifications.map(notification => ({
        ...notification,
        profiles: profiles?.find(p => p.id === notification.created_by) || null
      }));
    },
  });
}

export function useUpdateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateNotificationData> }) => {
      const { error } = await supabase
        .from("system_notifications")
        .update(data)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["system-notifications"] });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("system_notifications")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["system-notifications"] });
    },
  });
}

export function useToggleNotificationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from("system_notifications")
        .update({ is_active: isActive })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["system-notifications"] });
    },
  });
}